import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface LinkItem {
  path: string;
  title: string;
  description: string;
  category: string;
  tags?: string[];
  external?: boolean;
  status?: 'active' | 'beta' | 'deprecated' | 'legacy';
}

export default function LinksDirectory() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const internalLinks: LinkItem[] = [
    // Main Landing
    { path: '/', title: 'Home', description: 'Pousada Mastery Landing page', category: 'Main', tags: ['landing', 'home'] },

    // Pousada Suite
    { path: '/pousada', title: 'Pousada Knolling', description: 'Pousada management overview dashboard', category: 'Pousada', tags: ['hospitality', 'management'] },
    { path: '/pousada-ops', title: 'Pousada Operations', description: 'Operational management for pousada', category: 'Pousada', tags: ['operations', 'hospitality'] },
    { path: '/pousada-mobile', title: 'Pousada Mobile', description: 'Mobile interface for pousada management', category: 'Pousada', tags: ['mobile', 'hospitality'] },
    { path: '/pousada-ai', title: 'Pousada Mobile AI', description: 'AI-powered pousada assistant', category: 'Pousada', tags: ['ai', 'hospitality', 'automation'] },

    // Mastery Tools
    { path: '/mastery', title: 'Mobile Mastery', description: 'Mobile development mastery tools', category: 'Mastery', tags: ['mobile', 'development'] },
    { path: '/operator-mastery', title: 'Operator Mastery', description: 'Operations excellence dashboard', category: 'Mastery', tags: ['operations', 'excellence'] },
    { path: '/room', title: 'In-Room Experience', description: 'Guest room experience management', category: 'Mastery', tags: ['guest', 'experience'] },
    { path: '/unified', title: 'Unified Mastery', description: 'Unified control center', category: 'Mastery', tags: ['unified', 'control'] },

    // Development Tools
    { path: '/dev', title: 'Dev Dashboard', description: 'Developer tools and utilities', category: 'Development', tags: ['development', 'tools'], status: 'active' },
    { path: '/science', title: 'Science Dashboard', description: '🔬 Scientific debugging for all systems', category: 'Development', tags: ['debugging', 'diagnostics'], status: 'active' },
    { path: '/diagnostics', title: 'Diagnostics (alias)', description: 'Alternative path to Science Dashboard', category: 'Development', tags: ['debugging', 'diagnostics'] },
    { path: '/architecture', title: 'Architecture Diagram', description: 'System architecture visualization', category: 'Development', tags: ['architecture', 'diagram'] },
    { path: '/structure', title: 'Project Structure', description: 'Project file structure explorer', category: 'Development', tags: ['structure', 'files'] },
    { path: '/setup', title: 'Methodical Setup', description: 'Step-by-step setup wizard', category: 'Development', tags: ['setup', 'wizard'] },

    // Communication Tools
    { path: '/whatsapp', title: 'WhatsApp Sender', description: 'WhatsApp message sender', category: 'Communication', tags: ['whatsapp', 'messaging'] },
    { path: '/whatsapp-templates', title: 'WhatsApp Templates', description: 'Template-based WhatsApp messaging (ZapPilot)', category: 'Communication', tags: ['whatsapp', 'templates'], status: 'active' },
    { path: '/zappilot', title: 'ZapPilot', description: 'WhatsApp template bot interface', category: 'Communication', tags: ['whatsapp', 'bot'], status: 'active' },
    { path: '/whatsapp-tool', title: 'WhatsApp Setup Tool', description: 'WhatsApp integration setup', category: 'Communication', tags: ['whatsapp', 'setup'] },
    { path: '/telegram-tool', title: 'Telegram Setup Tool', description: 'Telegram bot setup wizard', category: 'Communication', tags: ['telegram', 'setup'] },
    { path: '/telegram-business', title: 'Telegram Business', description: 'Business Telegram dashboard', category: 'Communication', tags: ['telegram', 'business'] },
    { path: '/executive', title: 'Executive Dashboard', description: 'Executive Telegram dashboard', category: 'Communication', tags: ['telegram', 'executive'] },

    // Location Services
    { path: '/share', title: 'Location Share', description: 'Share your location with context', category: 'Location', tags: ['location', 'sharing'], status: 'active' },
    { path: '/global', title: 'Global Activity Map', description: 'Global activity visualization', category: 'Location', tags: ['map', 'global'] },
    { path: '/moderate', title: 'Moderation Dashboard', description: 'Content moderation interface', category: 'Location', tags: ['moderation', 'admin'] },
    { path: '/location', title: 'Location (legacy)', description: 'Legacy location pulse', category: 'Location', tags: ['location', 'legacy'], status: 'legacy' },
    { path: '/location-pulse', title: 'Location Pulse (legacy)', description: 'Legacy location pulse', category: 'Location', tags: ['location', 'legacy'], status: 'legacy' },
    { path: '/location-diagnostic', title: 'Location Diagnostic', description: 'Location service diagnostics', category: 'Location', tags: ['location', 'diagnostic'], status: 'legacy' },
    { path: '/location-pulse-diagnostic', title: 'Location Pulse Diagnostic', description: 'Detailed location diagnostics', category: 'Location', tags: ['location', 'diagnostic'], status: 'legacy' },

    // Business Tools
    { path: '/biig', title: 'Biig App', description: 'Main Biig application', category: 'Business', tags: ['biig', 'main'] },
    { path: '/biig-links', title: 'Biig Links', description: 'Biig resource links', category: 'Business', tags: ['biig', 'links'] },
    { path: '/projects', title: 'Projects Directory', description: 'All projects overview', category: 'Business', tags: ['projects', 'directory'] },
    { path: '/cto/*', title: 'CTO Dashboard', description: 'Chief Technology Officer dashboard', category: 'Business', tags: ['cto', 'management'] },
    { path: '/flywheel', title: 'Flywheel Dashboard', description: 'Business flywheel visualization', category: 'Business', tags: ['flywheel', 'metrics'] },
    { path: '/phase0', title: 'Phase 0 Tester', description: 'Phase 0 testing interface', category: 'Business', tags: ['testing', 'phase0'] },
    { path: '/phase1', title: 'Phase 1 Infrastructure', description: 'Infrastructure setup for Phase 1', category: 'Business', tags: ['infrastructure', 'phase1'] },
    { path: '/forge', title: 'Integration Forge', description: 'Integration development workshop', category: 'Business', tags: ['integration', 'development'] },
    { path: '/adapters', title: 'Adapter Manager', description: 'System adapters management', category: 'Business', tags: ['adapters', 'integration'] },

    // Problem & Work Management
    { path: '/problems', title: 'Problem Explorer', description: 'Problem tracking and management', category: 'Work', tags: ['problems', 'tracking'] },
    { path: '/problem/:identifier', title: 'Single Problem', description: 'Individual problem view (dynamic)', category: 'Work', tags: ['problems', 'detail'] },
    { path: '/questions', title: 'Problem Questions', description: 'Problem-related questions', category: 'Work', tags: ['problems', 'questions'] },
    { path: '/work', title: 'Work Buttons', description: 'One-click workplace automation', category: 'Work', tags: ['work', 'automation'] },
    { path: '/workbuttons', title: 'Work Buttons (alias)', description: 'Alternative path to Work Buttons', category: 'Work', tags: ['work', 'automation'] },

    // Discovery & Research
    { path: '/hidden-gems', title: 'Hidden Gems', description: 'Discover hidden features', category: 'Discovery', tags: ['discovery', 'features'] },
    { path: '/scraper', title: 'Reddit Scraper', description: 'Reddit content scraper dashboard', category: 'Discovery', tags: ['reddit', 'scraper'] },
    { path: '/research', title: 'Research Links', description: 'Research resource collection', category: 'Discovery', tags: ['research', 'resources'] },

    // Utilities
    { path: '/colors', title: 'Color Palettes', description: 'Color palette explorer', category: 'Utilities', tags: ['colors', 'design'] },
    { path: '/ai-connection-test', title: 'AI Connection Test', description: 'Test AI service connectivity', category: 'Utilities', tags: ['ai', 'testing'] },
  ];

  const externalLinks: LinkItem[] = [
    // APIs
    { path: 'http://localhost:8787', title: 'Local API', description: 'Local development API server', category: 'APIs', tags: ['api', 'local'], external: true },
    { path: 'http://localhost:8788', title: 'Local Files API', description: 'Local file storage API', category: 'APIs', tags: ['api', 'files', 'local'], external: true },
    { path: 'https://biig-api.b-9f2.workers.dev', title: 'Production API', description: 'Production API on Cloudflare Workers', category: 'APIs', tags: ['api', 'production'], external: true },
    { path: 'https://biig-web.pages.dev', title: 'Production Web', description: 'Production web app on Cloudflare Pages', category: 'APIs', tags: ['web', 'production'], external: true },

    // WhatsApp URLs
    { path: 'https://web.whatsapp.com', title: 'WhatsApp Web', description: 'WhatsApp Web interface', category: 'WhatsApp', tags: ['whatsapp', 'web'], external: true },
    { path: 'https://api.whatsapp.com', title: 'WhatsApp API', description: 'WhatsApp API for sending messages', category: 'WhatsApp', tags: ['whatsapp', 'api'], external: true },
    { path: 'https://wa.me', title: 'WhatsApp Direct', description: 'Direct WhatsApp chat links', category: 'WhatsApp', tags: ['whatsapp', 'direct'], external: true },

    // AWS Services
    { path: 'https://console.aws.amazon.com/iam', title: 'AWS IAM Console', description: 'AWS Identity and Access Management', category: 'AWS', tags: ['aws', 'iam'], external: true },
    { path: 'https://console.aws.amazon.com/ses', title: 'AWS SES Console', description: 'AWS Simple Email Service', category: 'AWS', tags: ['aws', 'ses', 'email'], external: true },

    // External Services
    { path: 'https://nominatim.openstreetmap.org', title: 'OpenStreetMap Nominatim', description: 'Geocoding and reverse geocoding service', category: 'Services', tags: ['maps', 'geocoding'], external: true },
    { path: 'https://api.dicebear.com', title: 'DiceBear Avatars', description: 'Avatar generation API', category: 'Services', tags: ['avatars', 'api'], external: true },
    { path: 'https://github.com/anthropics/claude-code/issues', title: 'Claude Code Issues', description: 'Report issues with Claude Code', category: 'Support', tags: ['claude', 'support'], external: true },
    { path: 'https://docs.anthropic.com/en/docs/claude-code', title: 'Claude Code Docs', description: 'Official Claude Code documentation', category: 'Documentation', tags: ['claude', 'docs'], external: true },
    { path: 'https://reactjs.org/link/error-boundaries', title: 'React Error Boundaries', description: 'React error boundary documentation', category: 'Documentation', tags: ['react', 'errors'], external: true },
  ];

  const allLinks = [...internalLinks, ...externalLinks];
  const categories = ['all', ...Array.from(new Set(allLinks.map(link => link.category)))];

  const filteredLinks = allLinks.filter(link => {
    const matchesSearch = searchTerm === '' ||
      link.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      link.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      link.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())) ||
      link.path.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = selectedCategory === 'all' || link.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const groupedLinks = filteredLinks.reduce((acc, link) => {
    if (!acc[link.category]) {
      acc[link.category] = [];
    }
    acc[link.category].push(link);
    return acc;
  }, {} as Record<string, LinkItem[]>);

  const getStatusBadge = (status?: LinkItem['status']) => {
    if (!status) return null;
    const colors = {
      active: 'bg-green-100 text-green-800',
      beta: 'bg-blue-100 text-blue-800',
      deprecated: 'bg-red-100 text-red-800',
      legacy: 'bg-gray-100 text-gray-800',
    };
    return (
      <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${colors[status]}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50 p-4 md:p-8">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                🔗 Links Directory
              </h1>
              <p className="text-lg text-gray-600">
                Complete directory of all routes, APIs, and external services
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-indigo-600">
                {filteredLinks.length}
              </div>
              <div className="text-sm text-gray-500">Total Links</div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">
                {internalLinks.length}
              </div>
              <div className="text-sm text-gray-600">Internal Routes</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">
                {externalLinks.length}
              </div>
              <div className="text-sm text-gray-600">External Links</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-purple-600">
                {categories.length - 1}
              </div>
              <div className="text-sm text-gray-600">Categories</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-orange-600">
                {allLinks.filter(l => l.status === 'active').length}
              </div>
              <div className="text-sm text-gray-600">Active Features</div>
            </div>
          </div>
        </header>

        {/* Search and Filters */}
        <div className="mb-6 bg-white p-4 rounded-lg shadow">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search links, descriptions, or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    selectedCategory === category
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category === 'all' ? 'All' : category}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Links Grid */}
        <div className="space-y-8">
          {Object.entries(groupedLinks).map(([category, links]) => (
            <div key={category} className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                {category}
                <span className="ml-3 text-sm font-normal bg-gray-100 px-2 py-1 rounded">
                  {links.length} {links.length === 1 ? 'link' : 'links'}
                </span>
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {links.map(link => (
                  <div
                    key={link.path}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all hover:border-indigo-300"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        {link.external ? (
                          <a
                            href={link.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-indigo-600 hover:text-indigo-800 flex items-center"
                          >
                            {link.title}
                            <svg
                              className="w-4 h-4 ml-1"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                              />
                            </svg>
                          </a>
                        ) : (
                          <Link
                            to={link.path}
                            className="font-semibold text-indigo-600 hover:text-indigo-800"
                          >
                            {link.title}
                          </Link>
                        )}
                        {getStatusBadge(link.status)}
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 mb-2">
                      {link.description}
                    </p>

                    <div className="text-xs text-gray-500 font-mono bg-gray-50 p-1 rounded mb-2 truncate">
                      {link.path}
                    </div>

                    {link.tags && link.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {link.tags.map(tag => (
                          <span
                            key={tag}
                            className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <footer className="mt-12 p-6 bg-white/70 rounded-xl text-center">
          <p className="text-sm text-gray-600 mb-2">
            💡 <strong>Pro Tip:</strong> Use the search to quickly find any route or service.
            Tags help you discover related features.
          </p>
          <p className="text-xs text-gray-500">
            Links Directory - Your complete navigation guide 🗺️
          </p>
        </footer>
      </div>
    </div>
  );
}